import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';
import { RequestState } from '../../models/RequestState';
import {
    GoogleDeviceStatusCount,
    GoogleDeviceCount
} from '../../models/CoreDeviceModels';
import {
    GetGoogleDeviceDashboardStatus,
    GetGoogleDeviceDashboardAutoUpdate,
    GetGoogleDeviceDashboardSchools,
    GetGoogleDeviceDashboardMissingUsers,
    GetGoogleDeviceDashboardOsVersions,
} from '../../store/GoogleDevice/GoogleDeviceDashboardAction';
import StatusCode from '../../util/StatusCode';
import TableHorizontal, { InfoObject } from '../misc/TableHorizontal';
import { Col, Row } from 'reactstrap';
import GoogleDeviceOverview from './GoogleDeviceOverview';


const GoogleDeviceDashboard = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation(['common', 'device']);

    const statuses = useSelector<RootState, RequestState<GoogleDeviceStatusCount[]>>(x => x.deviceDashboard.statuses);
    const missingUsers = useSelector<RootState, RequestState<GoogleDeviceCount>>(x => x.deviceDashboard.missingUsers);
    const autoUpdateExpiration = useSelector<RootState, RequestState<GoogleDeviceCount[]>>(x => x.deviceDashboard.autoUpdateExpiration);
    const schools = useSelector<RootState, RequestState<GoogleDeviceCount[]>>(x => x.deviceDashboard.schools);
    const osVersions = useSelector<RootState, RequestState<GoogleDeviceCount[]>>(x => x.deviceDashboard.osVersions);

    useEffect(() => {
        GetGoogleDeviceDashboardStatus()(dispatch);
        GetGoogleDeviceDashboardAutoUpdate()(dispatch);
        GetGoogleDeviceDashboardSchools()(dispatch);
        GetGoogleDeviceDashboardMissingUsers()(dispatch);
        GetGoogleDeviceDashboardOsVersions()(dispatch);
    }, [dispatch]);

    const statusInfo: InfoObject[] =
        statuses.code === StatusCode.COMPLETE
            ? statuses.data.map((x) => ({ id: x.status.toString(), data: [{ value: t(`googleDeviceListing.status.${x.status}`, { ns: 'device' }) }, { value: x.count }] })) : [];

    const renderStatuses = () => {
        return (<Col sm="6">
            <h3 className="mt-3">{t('dashboard.status.title', { ns: 'device' })}</h3>
            <TableHorizontal
                header={[
                    { value: t('dashboard.status.status', { ns: 'device' }) },
                    { value: t('dashboard.count', { ns: 'device' }) },
                ]}
                info={statusInfo}
                error={statuses.code === StatusCode.ERROR}
                searching={statuses.code === StatusCode.PENDING}
            />
        </Col>
        )
    }

    const autoUpdateInfo: InfoObject[] =
        autoUpdateExpiration.code === StatusCode.COMPLETE
            ? autoUpdateExpiration.data.map((x) => ({ id: x.name, data: [{ value: t(`dashboard.autoUpdateExpiration.ranges.${x.name}`, { ns: 'device' }) }, { value: x.count }] })) : [];

    const renderAutoUpdateExpirationInfo = () => {
        return (<Col sm="6">
            <h3 className="mt-3">{t('dashboard.autoUpdateExpiration.title', { ns: 'device' })}</h3>
            <TableHorizontal
                header={[
                    { value: t('dashboard.autoUpdateExpiration.range', { ns: 'device' }) },
                    { value: t('dashboard.count', { ns: 'device' }) },
                ]}
                info={autoUpdateInfo}
                error={autoUpdateExpiration.code === StatusCode.ERROR}
                searching={autoUpdateExpiration.code === StatusCode.PENDING}
            />
        </Col>
        )
    }

    const schoolInfo: InfoObject[] =
        schools.code === StatusCode.COMPLETE
            ? schools.data.map((x) => ({ id: x.name, data: [{ value: x.name === "missing" ? t('dashboard.schools.missing', { ns: 'device' }) : x.name }, { value: x.count }] })) : [];

    const renderSchoolInfo = () => {
        return (<Col sm="6">
            <h3 className="mt-3">{t('dashboard.schools.title', { ns: 'device' })}</h3>
            <TableHorizontal
                header={[
                    { value: t('dashboard.schools.school', { ns: 'device' }) },
                    { value: t('dashboard.count', { ns: 'device' }) },
                ]}
                info={schoolInfo}
                error={schools.code === StatusCode.ERROR}
                searching={schools.code === StatusCode.PENDING}
            />
        </Col>
        )
    }

    const missingUsersInfo: InfoObject[] =
        missingUsers.code === StatusCode.COMPLETE
            ? [{ id: missingUsers.data.name, data: [{ value: t('dashboard.missingUsers.name', { ns: 'device' }) }, { value: missingUsers.data.count }] }]
            : [];

    const renderMissingUsersInfo = () => {
        return (<Col sm="6">
            <h3 className="mt-3">{t('dashboard.missingUsers.title', { ns: 'device' })}</h3>
            <TableHorizontal
                header={[
                    { value: t('dashboard.missingUsers.title', { ns: 'device' }) },
                    { value: t('dashboard.count', { ns: 'device' }) },
                ]}
                info={missingUsersInfo}
                error={missingUsers.code === StatusCode.ERROR}
                searching={missingUsers.code === StatusCode.PENDING}
            />
        </Col>
        )
    }

    const osVersionsInfo: InfoObject[] =
        osVersions.code === StatusCode.COMPLETE
            ? osVersions.data.map((x) => ({ id: x.name, data: [{ value: x.name }, { value: x.count }] })) : [];

    const renderOsVersionsInfo = () => {
        return (<Col sm="6">
            <h3 className="mt-3">{t('dashboard.osVersion.title', { ns: 'device' })}</h3>
            <TableHorizontal
                header={[
                    { value: t('dashboard.osVersion.name', { ns: 'device' }) },
                    { value: t('dashboard.count', { ns: 'device' }) },
                ]}
                info={osVersionsInfo}
                error={osVersions.code === StatusCode.ERROR}
                searching={osVersions.code === StatusCode.PENDING}
            />
        </Col>
        )
    }

    return (<div className="container dashboard_oneroster padding-40">
        <h1>{t('dashboard.title', { ns: 'device' })}</h1>
        <GoogleDeviceOverview displayTitle={true} />
        <Row style={{ marginTop: '1rem' }}>
            {renderStatuses()}
            {renderAutoUpdateExpirationInfo()}
            {renderSchoolInfo()}
            {renderMissingUsersInfo()}
            {renderOsVersionsInfo()}
        </Row>
    </div>
    )
}

export default GoogleDeviceDashboard;
